/**
 * Phase 8 T-13 §13 A — Settings shell.
 *
 * Centered panel (`max-w-[1100px] mx-auto rounded-[12px] border`)
 * that splits into a 240 px left nav rail and a fluid content column.
 * All settings sub-routes render through `<Outlet />`.
 *
 * Mirrors Wrangle `210.png` / `225.png`.
 */

import { useCallback, useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  Building2,
  CreditCard,
  GitBranch,
  KeyRound,
  LayoutTemplate,
  LogOut,
  Plug,
  ShieldCheck,
  User,
  Users,
  Zap,
} from 'lucide-react';
import { useAuth } from '@core/auth/useAuth';
import { usePermissions } from '@core/auth/usePermissions';
import { orgService } from '@core/supabase/services/orgService';
import { PrivateStorageAvatar } from '@shared/components/ui/PrivateStorageAvatar';
import type { OrgUser } from '@core/types';

function nameInitials(name: string | null | undefined): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return 'U';
  return parts.length === 1
    ? parts[0][0].toUpperCase()
    : (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

type NavItem = {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  adminOnly?: boolean;
};

const NAV_ITEMS: NavItem[] = [
  { to: '/settings/profile',          label: 'Profile',          icon: User           },
  { to: '/settings/team',             label: 'Team',             icon: Users          },
  { to: '/settings/roles',            label: 'Roles',            icon: KeyRound        },
  { to: '/settings/branding',         label: 'Branding',         icon: Building2, adminOnly: true },
  { to: '/settings/integrations',     label: 'Integrations',     icon: Plug,       adminOnly: true },
  { to: '/settings/automation',       label: 'Automation',       icon: Zap,        adminOnly: true },
  { to: '/settings/billing',          label: 'Billing',          icon: CreditCard, adminOnly: true },
  { to: '/settings/email-templates',  label: 'Email templates',  icon: LayoutTemplate              },
  { to: '/settings/masking',          label: 'Masking',          icon: ShieldCheck                 },
  { to: '/settings/career-page',      label: 'Career page',      icon: GitBranch,  adminOnly: true },
];

/**
 * Shared outlet context so child pages can trigger the meOrgUser refresh.
 * (e.g. ProfileSettingsPage after avatar upload)
 */
export interface SettingsOutletContext {
  refreshMeOrgUser: () => void;
  meOrgUser: OrgUser | null;
}

export function SettingsLayout() {
  const { user, logout } = useAuth();
  const perms = usePermissions();
  const navigate = useNavigate();

  const [meOrgUser, setMeOrgUser] = useState<OrgUser | null>(null);

  const refreshMeOrgUser = useCallback(() => {
    if (!user?.orgId) { setMeOrgUser(null); return; }
    orgService
      .getTeamMembers(user.orgId)
      .then((members) => {
        const me = members.find(
          (m) => m.keycloak_sub === user.id || m.email === user.email,
        );
        setMeOrgUser(me ?? null);
      })
      .catch(() => setMeOrgUser(null));
  }, [user?.orgId, user?.id, user?.email]);

  useEffect(() => { refreshMeOrgUser(); }, [refreshMeOrgUser]);

  const visibleNav = NAV_ITEMS.filter(
    (item) => !item.adminOnly || perms.isAdmin || perms.isOwner,
  );

  const ctx: SettingsOutletContext = { refreshMeOrgUser, meOrgUser };

  return (
    /* page wrapper: push content away from topbar */
    <div className="w-full py-6 px-4 sm:px-6 min-h-[calc(100vh-48px)]">
      {/* centered panel */}
      <div className="max-w-[1100px] mx-auto rounded-[12px] border border-[var(--vscode-border-primary)] bg-[var(--vscode-bg-primary)] overflow-hidden shadow-sm">

        <div className="flex min-h-[600px]">

          {/* ── Left 240px nav rail ── */}
          <aside className="w-[240px] shrink-0 border-r border-[var(--vscode-border-primary)] bg-[color-mix(in_srgb,var(--vscode-bg-secondary)_60%,transparent)] flex flex-col">

            {/* Avatar + name cap */}
            <div className="p-4 border-b border-[var(--vscode-border-primary)]">
              <div className="flex items-center gap-3">
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 overflow-hidden text-sm font-bold"
                  style={{ background: 'color-mix(in srgb, #8B80F9 18%, var(--vscode-bg-tertiary))', color: '#8B80F9' }}
                >
                  {meOrgUser?.avatar_url ? (
                    <PrivateStorageAvatar
                      path={meOrgUser.avatar_url}
                      className="w-full h-full object-cover"
                      fallback={<span>{nameInitials(user?.name)}</span>}
                    />
                  ) : (
                    nameInitials(user?.name)
                  )}
                </div>
                <div className="min-w-0">
                  <p className="text-[13px] font-semibold vscode-text-primary truncate leading-tight">
                    {user?.name ?? user?.email ?? '—'}
                  </p>
                  <p className="text-[11px] vscode-text-tertiary truncate leading-tight">
                    {user?.orgName ?? ''}
                  </p>
                </div>
              </div>
            </div>

            {/* Nav items */}
            <nav className="flex-1 overflow-y-auto py-2" aria-label="Settings navigation">
              {visibleNav.map(({ to, label, icon: Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  className={({ isActive }) =>
                    [
                      'flex items-center gap-2.5 px-4 py-2 text-[13px] rounded-none transition-colors',
                      isActive
                        ? 'bg-[color-mix(in_srgb,#8B80F9_12%,transparent)] text-[#8B80F9] font-medium'
                        : 'vscode-text-secondary hover:vscode-text-primary hover:vscode-bg-hover',
                    ].join(' ')
                  }
                >
                  <Icon className="w-3.5 h-3.5 shrink-0" />
                  {label}
                </NavLink>
              ))}
            </nav>

            {/* Sign-out at the bottom */}
            <div className="border-t border-[var(--vscode-border-primary)] p-2">
              <button
                type="button"
                onClick={() => { logout(); navigate('/'); }}
                className="w-full flex items-center gap-2.5 px-4 py-2 text-[13px] text-red-500 hover:bg-red-50/60 dark:hover:bg-red-950/30 rounded-[8px] transition-colors text-left"
              >
                <LogOut className="w-3.5 h-3.5 shrink-0" />
                Sign out
              </button>
            </div>
          </aside>

          {/* ── Content column ── */}
          <main className="flex-1 min-w-0 p-6 overflow-y-auto">
            <Outlet context={ctx} />
          </main>

        </div>
      </div>
    </div>
  );
}
